import React, { useState } from 'react';
import { HeartPulse, Search, ArrowRight, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { hdfcPlans } from '../lib/policiesData';
import { useTranslation } from 'react-i18next';
import SEOHead from '../components/SEOHead';

const InsurancePlans = () => {
  const { t, i18n } = useTranslation();
  const isTe = i18n.language === 'te';
  const [searchTerm, setSearchTerm] = useState('');
  
  const filteredPlans = hdfcPlans.filter(plan => 
    plan.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (plan.nameTe && plan.nameTe.toLowerCase().includes(searchTerm.toLowerCase())) ||
    plan.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    plan.shortDesc.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (plan.shortDescTe && plan.shortDescTe.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const healthSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    'name': isTe ? 'భారతదేశంలో హెచ్‌డిఎఫ్‌సి ఎర్గో ఆరోగ్య బీమా పథకాలు | VSR Insurance Plus' : 'HDFC ERGO Health Insurance Plans in India | VSR Insurance Plus',
    'description': 'Compare HDFC ERGO health plans: Optima Secure (4X Cover), Optima Restore, and Optima Senior for parents. Free expert advice from V. S. Reddy.',
    'provider': {
      '@type': 'InsuranceAgency',
      'name': 'VSR Insurance Plus'
    }
  };

  return (
    <div className="min-h-screen pt-28 pb-16 bg-slate-50 dark:bg-slate-950">
      <SEOHead
        title={isTe ? "హెచ్‌డిఎఫ్‌సి ఎర్గో ఆరోగ్య బీమా పథకాలు | వి. ఎస్. రెడ్డి | VSR Insurance Plus" : "HDFC ERGO Health Insurance Plans in India | VSR Insurance Plus"}
        description={isTe ? "హెచ్‌డిఎఫ్‌సి ఎర్గో సమగ్ర ఆరోగ్య బీమా: ఆప్టిమా సెక్యూర్ (4 రెట్ల కవరేజ్), ఆప్టిమా రిస్టోర్, మరియు వృద్ధ తల్లిదండ్రుల కోసం ఆప్టిమా సీనియర్. 14,000+ ఆసుపత్రులలో నగదు రహిత సేవలు." : "Comprehensive HDFC ERGO health plans: Optima Secure, Optima Restore, and senior parent health insurance with zero room rent capping. Free consultation with V. S. Reddy."}
        canonicalPath="/insurance-plans"
        schemaJson={healthSchema}
        breadcrumbs={[{ name: isTe ? 'ఆరోగ్య బీమా' : 'Health Insurance', path: '/insurance-plans' }]}
      />

      <div className="container mx-auto px-6 max-w-6xl">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <div className="inline-flex items-center justify-center p-2.5 bg-rose-100 dark:bg-rose-900/30 rounded-full mb-4">
            <HeartPulse className="w-6 h-6 text-rose-600 dark:text-rose-500" />
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-slate-900 dark:text-white mb-4 tracking-tight">
            {t('hdfc_health_plans')}
          </h1>
          <p className="text-base text-slate-600 dark:text-slate-400 leading-relaxed">
            {t('hdfc_desc')} {isTe ? "దేశవ్యాప్తంగా 14,000 కంటే ఎక్కువ నెట్‌వర్క్ ఆసుపత్రులలో నగదు రహిత చికిత్స సదుపాయం." : "Cashless treatments across 14,000+ empanelled network hospitals in India."}
          </p>
        </div>

        {/* Search */}
        <div className="max-w-xl mx-auto mb-12 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
          <input 
            type="text" 
            placeholder={t('search_health_plans')} 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-6 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-rose-500 text-slate-900 dark:text-white text-sm"
          />
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredPlans.map(plan => (
            <div key={plan.id} className="bg-white dark:bg-slate-900 rounded-2xl p-6 border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-xl hover:border-rose-200 dark:hover:border-rose-900/50 transition-all duration-300 flex flex-col justify-between group">
              <div>
                <span className="inline-block px-3 py-1 bg-rose-50 dark:bg-rose-900/20 text-rose-600 dark:text-rose-400 rounded-full text-[10px] font-bold uppercase tracking-wider mb-4 border border-rose-100 dark:border-rose-900/30">
                  {isTe ? (plan.tagTe || plan.tag) : plan.tag}
                </span>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
                  {isTe ? (plan.nameTe || plan.name) : plan.name}
                </h3>
                <p className="text-slate-500 dark:text-slate-400 mb-5 text-sm leading-relaxed">
                  {isTe ? (plan.shortDescTe || plan.shortDesc) : plan.shortDesc}
                </p>
                
                <div className="space-y-2 mb-6">
                  {(isTe && plan.featuresTe ? plan.featuresTe : plan.features).slice(0, 4).map((feature, idx) => (
                    <div key={idx} className="flex gap-2 items-start text-xs text-slate-600 dark:text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-rose-500 flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between mt-auto">
                <div>
                  <div className="text-[11px] text-slate-400 font-medium">{t('cover_range')}</div>
                  <div className="text-sm font-bold text-slate-900 dark:text-white">
                    {isTe ? (plan.coverageTe || plan.coverage) : plan.coverage}
                  </div>
                </div>
                <Link to={`/plan/${plan.id}`} className="px-4 py-2 bg-rose-600 text-white rounded-lg text-sm font-semibold hover:bg-rose-700 transition-colors flex items-center gap-1.5 shadow-sm shadow-rose-200 dark:shadow-rose-900/20">
                  {t('view_plan')} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          ))}
        </div>
        
        {filteredPlans.length === 0 && (
          <div className="text-center py-20 text-slate-500 text-base">
            {t('no_plans_found')}
          </div>
        )}
      </div>
    </div>
  );
};

export default InsurancePlans;
